/**
 * Shared number, currency, percentage and date formatting helpers.
 *
 * There were 13 duplicate `formatCurrency` implementations scattered across the
 * codebase (dashboard widgets, trading components, CRM pages, the statement
 * generator, etc). See e.g.:
 *  - src/utils/formatters.ts (`formatCurrency`, `formatCurrencyAmount`, `formatAmount`)
 *  - src/services/statementGenerator.ts
 *  - src/components/shared/analytics/TradingMetricsSummary.tsx
 *  - src/components/shared/risk/RiskLimitConfiguration.tsx
 *  - src/pages/portal/wallet/AccountsPage.tsx
 *  - src/pages/crm/DashboardPage.tsx
 *  - src/pages/crm/trading/TradingAnalyticsTab.tsx / RiskManagementTab.tsx / ExecutionMonitoringTab.tsx
 *  - src/pages/portal/analytics/TradingAnalyticsPage.tsx / RiskAnalyticsPage.tsx
 *  - src/components/trading/* (PositionMetricsWidget, CompactPositionSummary, PositionWidget, ...)
 *  - src/components/dashboard/* (TradingMetricsWidget, RiskMonitoringWidget, CustomerTradingActivityWidget)
 * They differed mainly in: whether they handled non-ISO4217 currency codes (crypto
 * tickers) by falling back to a plain number + suffix, whether they used 0 or 2
 * fraction digits, and whether they tolerated `null`/`undefined`/string input.
 * This module is the single implementation covering that union of behaviours.
 * A later task migrates existing callers to import from here instead of
 * redefining a local formatter — the duplicates above are intentionally left
 * in place for now.
 *
 * None of the duplicates used `decimal.js` for arithmetic (they all format with
 * `Intl.NumberFormat` directly), so this module doesn't pull it in either.
 */

import { format as formatDateFns, formatDistanceToNow } from 'date-fns';

/** Returned for null/undefined/invalid input instead of "NaN" or an empty string. */
const EMPTY = '—';

type NumericInput = number | string | null | undefined;

/** Coerces a possibly-string, possibly-nullish value to a finite number, or null. */
function toFiniteNumber(value: NumericInput): number | null {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
}

export interface FormatCurrencyOptions {
    /** Fraction digits to show. Defaults to 2. */
    decimals?: number;
    /** Use compact notation for large numbers (e.g. "$1.2K", "$3.4M"). Defaults to false. */
    compact?: boolean;
    /** Always render a leading +/- sign (useful for P&L). Defaults to false. */
    signDisplay?: boolean;
}

/**
 * Format a value as currency.
 *
 * - Returns `'—'` for `null`/`undefined`/unparseable input rather than `'NaN'`.
 * - Accepts a numeric string (as returned by many APIs for money fields).
 * - Tolerates non-ISO4217 currency codes (crypto tickers like `BTC`) by falling
 *   back to a plain formatted number with the code appended, since
 *   `Intl.NumberFormat` throws for those.
 * - `compact: true` renders large numbers as `$1.2K` / `$3.4M` for KPI tiles.
 */
export function formatCurrency(
    value: NumericInput,
    currency: string = 'USD',
    options: FormatCurrencyOptions = {}
): string {
    const num = toFiniteNumber(value);
    if (num === null) return EMPTY;

    const { decimals = 2, compact = false, signDisplay = false } = options;
    const currencyCode = currency && currency.trim() !== '' ? currency : 'USD';

    const intlOptions: Intl.NumberFormatOptions = {
        minimumFractionDigits: compact ? 0 : decimals,
        maximumFractionDigits: compact ? Math.min(decimals, 2) : decimals,
        ...(compact ? { notation: 'compact', compactDisplay: 'short' } : {}),
        ...(signDisplay ? { signDisplay: 'exceptZero' } : {}),
    };

    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currencyCode,
            ...intlOptions,
        }).format(num);
    } catch {
        // Invalid ISO 4217 code (e.g. a crypto symbol) — format as a plain number
        // and append the code, matching the fallback used by the duplicate
        // implementations (src/utils/formatters.ts, statementGenerator.ts, ...).
        const formatted = new Intl.NumberFormat('en-US', intlOptions).format(num);
        return `${formatted} ${currencyCode}`;
    }
}

export interface FormatNumberOptions {
    /** Fraction digits to show. Defaults to 2. */
    decimals?: number;
    /** Use compact notation for large numbers (e.g. "1.2K"). Defaults to false. */
    compact?: boolean;
    /** Always render a leading +/- sign. Defaults to false. */
    signDisplay?: boolean;
}

/**
 * Format a plain number, tolerant of null/undefined/string input.
 * Returns `'—'` rather than `'NaN'` for invalid input.
 */
export function formatNumber(value: NumericInput, options: FormatNumberOptions = {}): string {
    const num = toFiniteNumber(value);
    if (num === null) return EMPTY;

    const { decimals = 2, compact = false, signDisplay = false } = options;

    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: compact ? 0 : decimals,
        maximumFractionDigits: compact ? 1 : decimals,
        ...(compact ? { notation: 'compact', compactDisplay: 'short' } : {}),
        ...(signDisplay ? { signDisplay: 'exceptZero' } : {}),
    }).format(num);
}

/**
 * Format a value as a percentage. `value` is the percentage itself (e.g. `45`
 * renders as `45.00%`), matching the existing `formatPercentage` convention —
 * it is not a 0-1 fraction.
 */
export function formatPercent(value: NumericInput, decimals: number = 2): string {
    const num = toFiniteNumber(value);
    if (num === null) return EMPTY;

    return new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(num / 100);
}

export type DateFormat = 'short' | 'long' | 'datetime' | 'time' | 'relative';

const DATE_FNS_PATTERN: Record<Exclude<DateFormat, 'relative'>, string> = {
    short: 'dd/MM/yyyy',
    long: 'd MMMM yyyy',
    datetime: 'dd/MM/yyyy HH:mm',
    time: 'HH:mm',
};

/**
 * Format a date. Accepts an ISO string or `Date`; returns `'—'` for
 * `null`/`undefined`/unparseable input.
 *
 * - `'short'` (default): `25/09/2026`
 * - `'long'`: `25 September 2026`
 * - `'datetime'`: `25/09/2026 14:30`
 * - `'time'`: `14:30`
 * - `'relative'`: `2 hours ago` (via date-fns `formatDistanceToNow`)
 */
export function formatDate(
    value: string | Date | null | undefined,
    format: DateFormat = 'short'
): string {
    if (value === null || value === undefined || value === '') return EMPTY;

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return EMPTY;

    if (format === 'relative') {
        return formatDistanceToNow(date, { addSuffix: true });
    }

    return formatDateFns(date, DATE_FNS_PATTERN[format]);
}
